import {
  Box,
  Button,
  Fieldset,
  FileButton,
  Group,
  Select,
  Text,
  TextInput,
  Transition,
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './soil-map-form.module.css';
import { useSoilMapForm } from './use-soil-map-form';

import { MAP_TYPES, useUploadSoilMap } from '@fields/data-access';
import { TsrButtonLink } from '@ui';
import { ProcessSoilMapDto } from 'modules/fields/data-access/src/lib/types/dto';

interface SoilMapFormProps {
  loading?: boolean;
  fieldId: number;
  onSubmit: (values: ProcessSoilMapDto) => void;
}

export function SoilMapForm({ loading, fieldId, onSubmit }: SoilMapFormProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const form = useSoilMapForm(fieldId);

  const uploadFile = useUploadSoilMap();

  const handleFileChange = (f: File | null) => {
    if (f) {
      form.reset();
      setFile(f);
      uploadFile.mutate(
        {
          fieldId,
          file: f,
        },
        {
          onSuccess: (data) => {
            if (data.map_id) {
              form.setFieldValue('map_id', data.map_id);
            }
          },
        },
      );
    }
  };

  return (
    <form className={classes.form} onSubmit={form.onSubmit(onSubmit)}>
      <Box className={classes.inputsContainer}>
        <FileButton accept="zip" onChange={handleFileChange}>
          {(props) => (
            <Button {...props} loading={uploadFile.isPending}>
              {t('field.form.fileLabel')}
            </Button>
          )}
        </FileButton>
        {file ? (
          <Text mt="sm" size="sm">
            {t('field.form.fileSelected')} {file.name}
          </Text>
        ) : null}

        <Transition
          duration={400}
          mounted={!!uploadFile.data}
          timingFunction="ease"
          transition="fade"
        >
          {(styles) => (
            <Fieldset
              disabled={!uploadFile.data}
              mt="sm"
              style={styles}
              variant="filled"
            >
              <TextInput
                label={t('field.form.mapNameLabel')}
                {...form.getInputProps('name')}
              />

              <Select
                data={uploadFile.data?.column_name}
                label={t('field.form.columnNameLabel')}
                mt="sm"
                required
                {...form.getInputProps('column_name')}
              />

              <Select
                data={uploadFile.data?.soil_map_type}
                label={t('field.form.soilMapTypeLabel')}
                mt="sm"
                required
                {...form.getInputProps('soil_map_type')}
              />
            </Fieldset>
          )}
        </Transition>
      </Box>

      <Group justify="start">
        <Button loading={loading} type="submit">
          {t('common.add')}
        </Button>
        <TsrButtonLink
          color="red"
          params={{
            fieldId: fieldId.toString(),
            mapType: MAP_TYPES.SOIL,
          }}
          to="/fields/$fieldId/maps/$mapType"
        >
          {t('common.back')}
        </TsrButtonLink>
      </Group>
    </form>
  );
}

export default SoilMapForm;
